import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/shared/infra/query'
import { ThemeProvider } from '@/shared/ui/providers/theme-provider'
import { ToastProvider } from '@/shared/ui/providers/toast-provider'
import { PersonaProvider } from '@/shared/ui/providers/persona-provider'
import { LicenseCatalogProvider } from '@/shared/ui/providers/license-catalog-provider'
import { UnitsProvider } from '@/shared/ui/providers/units-provider'
import { UploadManagerProvider } from '@/shared/ui/providers/upload-manager-provider'
import { AuthProvider } from '@/modules/auth/ui/providers/auth-provider'
import { makeLoadSession } from '@/main/factories/modules/auth/make-load-session'
import { makeSignOut } from '@/main/factories/modules/auth/make-sign-out'
import { makeOnAuthStateChange } from '@/main/factories/modules/auth/make-on-auth-state-change'
import { makeLoadAccessToken } from '@/main/factories/modules/auth/make-load-access-token'
import { makeOnAccessTokenChange } from '@/main/factories/modules/auth/make-on-access-token-change'
import { makeLoadUserProfile } from '@/main/factories/modules/auth/make-load-user-profile'
import { makeUpdateMyProfile } from '@/main/factories/modules/auth/make-update-my-profile'
import { makeRevokeImpersonation } from '@/main/factories/modules/auth/make-revoke-impersonation'
import { makeRegisterTabCloseRevoke } from '@/main/factories/modules/auth/make-tab-close-revoke'
import { makeLoadMemberContext } from '@/main/factories/shared/make-load-member-context'
import { makeLoadLicenseCatalog } from '@/main/factories/shared/make-load-license-catalog'
import { makeUploadGalleryVideo } from '@/main/factories/modules/gallery/make-upload-gallery-video'

const loadSession = makeLoadSession()
const signOut = makeSignOut()
const onAuthStateChange = makeOnAuthStateChange()
const loadMemberContext = makeLoadMemberContext()
const loadAccessToken = makeLoadAccessToken()
const onAccessTokenChange = makeOnAccessTokenChange()
const uploadGalleryVideo = makeUploadGalleryVideo()
const loadUserProfile = makeLoadUserProfile()
const updateMyProfile = makeUpdateMyProfile()
const loadLicenseCatalog = makeLoadLicenseCatalog()
const revokeImpersonation = makeRevokeImpersonation()
const registerTabCloseRevoke = makeRegisterTabCloseRevoke()

type AppProvidersProps = {
  children: React.ReactNode
}

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider
            onLoadSession={loadSession}
            onSignOut={signOut}
            onAuthStateChange={onAuthStateChange}
            onLoadAccessToken={loadAccessToken}
            onAccessTokenChange={onAccessTokenChange}
          >
            <UnitsProvider onLoadUserProfile={loadUserProfile} onUpdateMyProfile={updateMyProfile}>
              <PersonaProvider
                onLoadMemberContext={loadMemberContext}
                onRevokeImpersonation={revokeImpersonation}
                onRegisterTabCloseRevoke={registerTabCloseRevoke}
              >
                <LicenseCatalogProvider onLoadLicenseCatalog={loadLicenseCatalog}>
                  <UploadManagerProvider onUploadGalleryVideo={uploadGalleryVideo}>
                    {children}
                  </UploadManagerProvider>
                </LicenseCatalogProvider>
              </PersonaProvider>
            </UnitsProvider>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
